import React from 'react';
import 'dayjs/locale/pt-br';
import { CircularProgress } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PopupBase from '../../../../../popup/base/PopupBase';
import CustomButton from '../../../../../components/buttons/CustomButton';
import { isValidDate } from './documentFormat';
import styles from './DocumentForm.module.css';

/**
 * Casca dos formulários de pedido médico e guia: PopupBase com o conteúdo em
 * <form>, erro geral no topo, Enter num campo envia e o rodapé com Cancelar e
 * Salvar. Os campos vêm como children, agrupados em <FormSection>.
 */
export function DocumentFormPopup({ title, submitLabel, saving, generalError, onSubmit, onClose, children }) {
  const submit = (event) => {
    if (event) event.preventDefault();
    if (!saving) onSubmit();
  };

  const content = (
    <form className={styles.content} onSubmit={submit} noValidate>
      {generalError && <div className={styles.error} role="alert">{generalError}</div>}
      {children}
      <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
    </form>
  );

  const footer = (
    <div className={styles.footer}>
      <CustomButton style="primary" variant="text" label="Cancelar" onClick={onClose} enabled={!saving} />
      <CustomButton
        style="main"
        variant="contained"
        label={submitLabel}
        icon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        onClick={submit}
        enabled={!saving}
      />
    </div>
  );

  return <PopupBase Title={title} Content={content} Footer={footer} OnClose={onClose} maxWidth="sm" />;
}

export function FormSection({ title, children }) {
  return (
    <section className={styles.section}>
      {title && <h4 className={styles.sectionTitle}>{title}</h4>}
      {children}
    </section>
  );
}

/** Rótulo acima do campo, no padrão dos formulários de configuração. */
export function Field({ label, htmlFor, children }) {
  return (
    <div className={styles.field}>
      {label && <label className={styles.label} htmlFor={htmlFor}>{label}</label>}
      {children}
    </div>
  );
}

/** Dois campos lado a lado; vira coluna única na janela estreita. */
export function FieldPair({ children }) {
  return <div className={styles.pair}>{children}</div>;
}

/**
 * Data com o calendário do projeto. `value` e `onChange` trabalham com dayjs
 * (ou null); a conversão para a API fica em documentFormat.toApiDate.
 * A data parcial da digitação (dayjs inválido) volta como está: trocá-la por
 * null apagava dia e mês ao digitar por cima. Quem valida é o formulário, ao salvar.
 * `ariaLabel`: nome acessível quando o campo não tem rótulo próprio (htmlFor).
 */
export function DateField({ id, value, onChange, disabled, error, helperText, minDate, ariaLabel }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <DatePicker
        value={value || null}
        onChange={(next) => onChange(next || null)}
        disabled={disabled}
        minDate={isValidDate(minDate) ? minDate : undefined}
        format="DD/MM/YYYY"
        slotProps={{
          textField: {
            id,
            size: 'small',
            fullWidth: true,
            error: !!error,
            helperText: error || helperText,
            className: styles.input,
            ...(ariaLabel ? { inputProps: { 'aria-label': ariaLabel } } : {}),
          },
        }}
      />
    </LocalizationProvider>
  );
}

export const formStyles = styles;
